import { AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { cn } from "@/lib/utils";
import { IS_PRODUCTION, ENV_LABEL } from "@/config/version";
import { useDevModelMode } from "@/lib/devModelMode";
import { DevModelMode } from "@shared/schemas/enums";
import { ENV_BANNER } from "./constants";

/**
 * Full-width red strip pinned to the top of the viewport on every
 * NON-production environment (local `npm run dev` and the dev.todowith.ai
 * preview deploy). It is `fixed`, so it stays visible while scrolling on
 * every page. It is intentionally loud so it's impossible to mistake a
 * development environment for production.
 *
 * It also hosts the dev-only **Models** toggle (Cheap ⇄ Normal). Cheap
 * (default) forces every flow — including the ones with no model picker
 * (Talking Avatar, Baseball Trend, …) — onto the cheap override models so
 * dev stays ~free; Normal runs the real models for quality testing. The
 * choice rides every API request as a header (see lib/devModelMode.ts) and
 * is enforced server-side; it is ignored entirely in production.
 *
 * Renders nothing on the real production deployment (app.todowith.ai).
 * The page reserves `ENV_BANNER.pagePadding` of space so this never overlaps
 * content — keep that constant in sync if the height changes.
 */
export function EnvBanner() {
  const { t } = useLanguage();
  const [mode, setMode] = useDevModelMode();

  if (IS_PRODUCTION) return null;

  const options = [
    { value: DevModelMode.CHEAP, label: t.nav.devModelsCheap },
    { value: DevModelMode.NORMAL, label: t.nav.devModelsNormal },
  ] as const;

  return (
    <div
      role="status"
      aria-live="polite"
      className={cn(
        "fixed top-0 inset-x-0 z-[60] w-full bg-red-600 text-white px-4 flex items-center justify-center gap-3 text-[11px] sm:text-xs font-bold uppercase tracking-widest",
        ENV_BANNER.height,
      )}
    >
      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
      <span className="truncate">
        {t.nav.devEnvironment} · {ENV_LABEL}
      </span>

      <span
        className="flex items-center gap-1 normal-case tracking-normal"
        role="group"
        aria-label={t.nav.devModelsLabel}
      >
        <span className="hidden sm:inline opacity-80">{t.nav.devModelsLabel}:</span>
        <span className="flex overflow-hidden rounded-full border border-white/40">
          {options.map((opt) => (
            <button
              key={opt.value}
              type="button"
              onClick={() => setMode(opt.value)}
              aria-pressed={mode === opt.value}
              className={cn(
                "px-2 py-0.5 text-[10px] font-semibold leading-none transition-colors",
                mode === opt.value
                  ? "bg-white text-red-600"
                  : "text-white/80 hover:bg-white/15",
              )}
            >
              {opt.label}
            </button>
          ))}
        </span>
      </span>

      <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
    </div>
  );
}
